import { createBrowserHistory } from "history"
import { TypedUseSelectorHook, useSelector } from "react-redux"
import { AnyAction, combineReducers } from "redux"
import { createReduxHistoryContext } from "redux-first-history"

import { Dispatcher } from "../api/dispatcher"
import { api } from "./api"
import { appReducer } from "./app/appSlice"
import { appointmentsReducer } from "./appointments/appointmentsSlice"
import { appParamsReducer } from "./appParams/appParamsSlice"
import { authReducer, clearAuth } from "./auth/authSlice"
import { autoScanRoomsReducer } from "./autoScanRooms/autoScanRoomsSlice"
import { bookAssetReducer } from "./book_asset/bookAssetSlice"
import { eBadgeScanReducer } from "./eBadgeScan/eBadgeScanSlice"
import { filesReducer } from "./files/filesSlice"
import {
  apiErrorHandlingMiddleware,
  offlineMiddleware,
  sentryMiddleware,
} from "./middlewares"
import { mobileAppReducer } from "./mobileApp/mobileAppSlice"
import { notificationMenuReducer } from "./notificationMenu/notificationMenuSlice"
import { Feature, paymentReducer, setHasPaid } from "./payment/paymentSlice"
import { reservationsReducer } from "./reservations/reservationsSlice"
import { screeningsReducer } from "./screenings/screeningsSlice"
import { shareableReducer } from "./shareable/shareableSlice"
import { ssoProvidersReducer } from "./ssoProviders/ssoProvidersSlice"
import { suggestionsReducer } from "./suggestions/suggestionsSlice"
import { tabletReducer } from "./tablet/tabletSlice"
import { userReducer } from "./user/userSlice"
import { usersReducer } from "./users/usersSlice"
import { visitorEmailSettingsReducer } from "./visitor_email_settings/visitorEmailSettingsSlice"
import { visitorLoginReducer } from "./visitor_login/visitorLoginSlice"
import { visitorRegistrationReducer } from "./visitor_registration/visitorRegistrationSlice"
import { configureStore } from "@reduxjs/toolkit"
import { setupListeners } from "@reduxjs/toolkit/query"

const browserHistory = createBrowserHistory()

const { createReduxHistory, routerMiddleware, routerReducer } =
  createReduxHistoryContext({
    history: browserHistory,
  })

export function logger(state = null, action: AnyAction) {
  if (typeof action.type === "string" && action.type.startsWith("api/")) {
    const { meta } = action
    return {
      ...action,
      meta: {
        ...meta,
        baseQueryMeta: {
          ...(meta?.baseQueryMeta ?? {}),
          request: meta?.baseQueryMeta?.request?.url ?? "",
          response: meta?.baseQueryMeta?.response?.url ?? "",
        },
      },
    }
  }

  if (
    typeof action.type === "string" &&
    action.type.includes("uploadSignature")
  ) {
    const { meta } = action
    return {
      ...action,
      meta: {
        ...meta,
        arg: {
          ...meta.arg,
          file: {
            ...meta.arg.file,
            lastModifiedDate: meta?.arg?.file.lastModifiedDate?.toISOString(),
          },
        },
      },
    }
  }

  return action
}

// Combine all reducers
const rootReducer = combineReducers({
  [api.reducerPath]: api.reducer,
  app: appReducer,
  appointments: appointmentsReducer,
  appParams: appParamsReducer,
  auth: authReducer,
  autoScanRooms: autoScanRoomsReducer,
  bookAsset: bookAssetReducer,
  files: filesReducer,
  mobileApp: mobileAppReducer,
  eBadgeScan: eBadgeScanReducer,
  lastAction: logger,
  payment: paymentReducer,
  reservations: reservationsReducer,
  router: routerReducer,
  screenings: screeningsReducer,
  shareable: shareableReducer,
  ssoProviders: ssoProvidersReducer,
  suggestions: suggestionsReducer,
  tablet: tabletReducer,
  user: userReducer,
  users: usersReducer,
  visitor_login: visitorLoginReducer,
  visitorEmailSettings: visitorEmailSettingsReducer,
  visitorRegistration: visitorRegistrationReducer,
  notificationMenu: notificationMenuReducer,
})

// Configure store
export const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: false,
      immutableCheck: {
        // Ignore File objects in RTK Query actions to prevent immutability errors
        ignoredPaths: [
          "lastAction.meta.arg.originalArgs",
          "lastAction.meta.arg.lastModifiedDate",
        ],
      },
    })
      .concat(routerMiddleware)
      .concat(api.middleware)
      .concat(apiErrorHandlingMiddleware)
      .concat(offlineMiddleware)
      .concat(sentryMiddleware),
  preloadedState: {},
})

/**
 * RTK Query dispatches the focus/online actions behind refetchOnFocus and
 * refetchOnReconnect only once listeners are attached. Without this call those
 * options are silently ignored everywhere they are set.
 */
setupListeners(store.dispatch)

// Create and export history
export const historyObject = createReduxHistory(store)

// Dispatcher support
const dispatcher = Dispatcher.getInstance()

dispatcher.on("clearAuth", () => {
  store.dispatch(clearAuth())
})

dispatcher.on("setHasPaid", (feature: Feature) => {
  store.dispatch(setHasPaid(feature))
})

// Types
export type RootState = ReturnType<typeof store.getState>
export type ReducerKeys = keyof RootState
export type ReducerValues = RootState[keyof RootState]
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector
export type AppDispatch = typeof store.dispatch

export type ExtendedReducerKeys = keyof RootState
